/**
 * Supabase client configuration
 * Uses localStorage for session persistence in Electron renderer process.
 *
 * IMPORTANT: This module must NEVER throw synchronously at import time.
 * The renderer process (and therefore React) will fail to mount if it does,
 * which manifests as a blank window in packaged builds. Resolution order
 * (handled inside `getEnvVar`):
 *   1. The IPC bridge (`window.electron.ipcRenderer.getEnv`) — populated
 *      at runtime from the main-process `.env` lookup.
 *   2. The renderer's build-time env table (`util/index.ts` BUILD_TIME_ENV)
 *      whose entries are inlined by webpack's EnvironmentPlugin from the
 *      project `.env`, so the packaged renderer always has a working
 *      public anon key and project URL even with no runtime env file.
 *   3. Hard-coded public defaults — last-resort fallback so the client
 *      can at least be constructed (the anon key is a public client key
 *      intended to be shipped with apps; row-level security in Supabase
 *      protects data).
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { getEnvVar } from '../util';

const DEFAULT_SUPABASE_URL = 'https://dfqmwjbmmvudxrmuiafv.supabase.co';

export const resolveSupabaseUrl = (): string => {
  return getEnvVar('SUPABASE_URL') || DEFAULT_SUPABASE_URL;
};

export const resolveSupabaseKey = (): string => {
  return (
    getEnvVar('SUPABASE_PUBLISHABLE_API_KEY') ||
    getEnvVar('SUPABASE_ANON_KEY') ||
    ''
  );
};

// Placeholder key used only when no real key is available. createClient()
// validates that a non-empty string is passed and throws otherwise; passing
// this string keeps the renderer process alive so React can mount and show a
// useful error UI instead of a blank window. All API calls will fail with an
// auth error, which is recoverable; a blank window is not.
const MISSING_KEY_PLACEHOLDER = 'missing-supabase-anon-key';

const createSupabaseClient = (): SupabaseClient => {
  const url = resolveSupabaseUrl();
  const key = resolveSupabaseKey();

  if (!key) {
    // eslint-disable-next-line no-console
    console.error(
      '[supabase] No SUPABASE_ANON_KEY / SUPABASE_PUBLISHABLE_API_KEY found. ' +
        'The Supabase client will be created with a placeholder key and all ' +
        'auth/data calls will fail. Ensure the renderer build embeds the key ' +
        'via webpack EnvironmentPlugin or that the main-process .env loader ' +
        'finds a .env file at runtime.',
    );
  }

  return createClient(url, key || MISSING_KEY_PLACEHOLDER, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // Always false: we exchange PKCE codes explicitly in App.tsx so we
      // control exactly when the exchange happens. Relying on Supabase's
      // automatic detection caused a race between its internal attempt and
      // the getSession() call in App.tsx, resulting in the code verifier
      // being consumed/missing on the second request → 400 validation_failed.
      detectSessionInUrl: false,
    },
  });
};

export const supabase = createSupabaseClient();
